import { Link } from "react-router-dom";
import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Clock, ArrowRight, ArrowUpRight } from "lucide-react";
import { CASES } from "@/content/cases";

const CATEGORIES = [
  { title: "Skin Quality", desc: "Texture, luminosità e uniformità della pelle." },
  { title: "Cicatrici da acne", desc: "Miglioramento di superficie e avvallamenti." },
  { title: "Macchie e discromie", desc: "Uniformità del colorito e delle pigmentazioni." },
  { title: "Trattamenti vascolari", desc: "Couperose, capillari e rossori del viso." },
  { title: "Ringiovanimento del viso", desc: "Percorsi combinati non chirurgici." },
  { title: "Tricologia", desc: "Densità e qualità del capello nel tempo." },
];

const STEPS = [
  { n: "01", t: "Problema iniziale", d: "La condizione di partenza descritta in modo chiaro e oggettivo." },
  { n: "02", t: "Valutazione", d: "L'analisi clinica: cosa determina l'inestetismo e cosa è realmente trattabile." },
  { n: "03", t: "Protocollo", d: "La strategia scelta e il perché, con obiettivi realistici." },
  { n: "04", t: "Trattamenti eseguiti", d: "Tecnologie e procedure utilizzate, eventualmente combinate." },
  { n: "05", t: "Sedute e tempi", d: "Numero di sedute, intervalli e rivalutazioni del percorso." },
  { n: "06", t: "Risultato e nota realistica", d: "L'esito ottenuto e i fattori individuali che lo influenzano." },
];

const ease = [0.22, 1, 0.36, 1];

export function RisultatiPage() {
  const categories = useMemo(() => Array.from(new Set(CASES.map((c) => c.category))), []);
  const [active, setActive] = useState("Tutti");
  const filtered = active === "Tutti" ? CASES : CASES.filter((c) => c.category === active);

  return (
    <div className="bg-canvas text-ink" data-testid="risultati-page">
      <div className="max-w-5xl mx-auto px-6 md:px-10 pt-36 pb-28">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }}>
          <p className="text-[11px] uppercase tracking-[0.35em] text-sagedark mb-5">Percorsi clinici · Prima e dopo</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="risultati-h1">
            Percorsi e Risultati
          </h1>
          <p className="mt-6 font-serif text-xl md:text-2xl font-light text-ink/70 max-w-2xl">
            Casi clinici spiegati come percorsi personalizzati, non semplici fotografie
          </p>
          <p className="mt-6 text-[15px] md:text-base leading-relaxed text-ink/65 max-w-3xl">
            Ogni risultato nasce da un percorso: partiamo dal problema iniziale, passiamo per la
            valutazione medica e il protocollo scelto, fino al risultato ottenuto. Le immagini
            prima/dopo vengono sempre presentate nel loro contesto clinico, perché indicazioni,
            tempi e risposta ai trattamenti sono individuali.
          </p>
        </motion.div>

        {CASES.length > 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.12 }} className="mt-16">
            <p className="text-[11px] uppercase tracking-[0.35em] text-sagedark mb-6">Percorsi documentati</p>
            {categories.length >= 1 && (
              <div className="flex flex-wrap gap-2.5 mb-8" data-testid="case-filters">
                {["Tutti", ...categories].map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActive(cat)}
                    data-testid={`case-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                    aria-pressed={active === cat}
                    className={`px-4 py-2 text-[12px] uppercase tracking-[0.12em] border transition-colors duration-300 ${
                      active === cat ? "bg-ink text-canvas border-ink" : "border-ink/15 text-ink/60 hover:border-sage hover:text-ink"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
              {filtered.map((c) => (
                <div key={c.slug} className="flex flex-col bg-canvas p-8 md:p-10" data-testid={`case-card-${c.slug}`}>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-sagedark">{c.category}</span>
                  <h2 className="mt-3 font-serif text-2xl md:text-3xl font-light leading-tight">{c.title}</h2>
                  <p className="mt-3 font-serif text-lg font-light text-ink/60">{c.protocol}</p>
                  {c.summary && c.summary["Rivalutazione fotografica"] && (
                    <p className="mt-5 inline-flex items-center gap-2 text-[13px] text-ink/55">
                      <Clock className="w-4 h-4 text-sagedark" />
                      Rivalutazione a {c.summary["Rivalutazione fotografica"]}
                    </p>
                  )}
                  <Link to={`/risultati/${c.slug}`} data-testid={`case-link-${c.slug}`} className="mt-7 group relative inline-flex w-fit items-center overflow-hidden border border-ink px-6 py-3 text-xs uppercase tracking-[0.15em]">
                    <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
                    <span className="relative group-hover:text-canvas transition-colors duration-500 inline-flex items-center gap-2">Vedi il percorso <ArrowUpRight className="w-4 h-4" /></span>
                  </Link>
                </div>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.12 }} className="mt-14 border border-ink/10 bg-sand/40 p-8 md:p-12">
            <span className="inline-flex items-center gap-2 border border-sagedark/40 text-sagedark px-4 py-1.5 text-[11px] uppercase tracking-[0.2em]" data-testid="risultati-badge">
              <Clock className="w-3.5 h-3.5" />
              Sezione in aggiornamento
            </span>
            <h2 className="mt-7 font-serif text-2xl md:text-3xl font-light">Stiamo preparando i primi percorsi documentati.</h2>
            <p className="mt-5 text-[15px] md:text-base leading-relaxed text-ink/70 max-w-3xl">
              A breve raccoglieremo qui casi reali, con immagini prima e dopo, protocollo terapeutico
              e tecnologie utilizzate, per mostrare in modo trasparente l'evoluzione dei trattamenti
              nel tempo — sempre con una nota realistica sui risultati.
            </p>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.2 }} className="mt-20">
          <p className="text-[11px] uppercase tracking-[0.35em] text-sagedark mb-8">Come sarà strutturato ogni caso</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-canvas p-7 min-h-[150px]" data-testid={`risultati-step-${s.n}`}>
                <span className="font-serif text-2xl text-sage">{s.n}</span>
                <h3 className="mt-3 font-serif text-lg font-light">{s.t}</h3>
                <p className="mt-2 text-sm text-ink/55 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.28 }} className="mt-20">
          <p className="text-[11px] uppercase tracking-[0.35em] text-sagedark mb-8">Categorie in preparazione</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            {CATEGORIES.map((c) => (
              <div key={c.title} data-testid={`risultati-category-${c.title.toLowerCase().replace(/\s+/g, "-")}`} className="bg-canvas p-7 flex flex-col min-h-[150px]">
                <span className="text-[10px] uppercase tracking-[0.25em] text-ink/35 mb-3">In preparazione</span>
                <h3 className="font-serif text-xl font-light">{c.title}</h3>
                <p className="mt-2 text-sm text-ink/55 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.36 }} className="mt-16 pt-10 border-t border-ink/10 flex flex-col sm:flex-row sm:items-center gap-6">
          <p className="text-[15px] text-ink/60 max-w-xl">
            Nel frattempo puoi richiedere una valutazione medica per capire quale percorso è indicato
            nel tuo caso specifico.
          </p>
          <Link to="/contatti" data-testid="risultati-cta" className="group inline-flex items-center gap-2 border border-ink px-8 py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-ink hover:text-canvas transition-colors duration-500 shrink-0 w-fit">
            Prenota una valutazione
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
